// client/src/utils/accountLinkStorage.js
import { deleteDoc, doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase';

const STORAGE_KEY = 'ecobillLinkedAccounts_v1';
const storageKey = (uid) => (uid ? `${STORAGE_KEY}_${uid}` : STORAGE_KEY);
export const getLinkedAccountsStorageKey = storageKey;
export const LINKED_ACCOUNTS_STORAGE_KEY = STORAGE_KEY;
export const MAX_LINKED_ACCOUNTS = 10;

const hasWindow = () => typeof window !== 'undefined' && !!window.localStorage;
const accountsDoc = (uid) => doc(firestore, 'accountLinks', uid);

const sanitizeEntries = (list = [], ownerUid = null) => {
  const normalized = (Array.isArray(list) ? list : [])
    .filter((entry) => entry && entry.uid && entry.email && entry.secret)
    .map((entry) => ({
      uid: entry.uid,
      email: entry.email,
      pseudo: entry.pseudo || '',
      role: entry.role || '',
      secret: entry.secret,
      addedAt: entry.addedAt || Date.now(),
      lastUsedAt: entry.lastUsedAt || Date.now(),
      isOwner: entry.isOwner === true || entry.uid === entry.ownerUid || entry.uid === ownerUid,
    }));

  const ownerEntry = ownerUid
    ? normalized.find((entry) => entry.uid === ownerUid || entry.isOwner)
    : null;

  const others = normalized.filter((entry) => entry !== ownerEntry);
  const trimmedOthers = others.slice(0, MAX_LINKED_ACCOUNTS);

  return ownerEntry ? [ { ...ownerEntry, isOwner: true }, ...trimmedOthers ] : trimmedOthers;
};

const readRaw = (ownerUid) => {
  if (!hasWindow()) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(ownerUid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.accounts)) return parsed.accounts;
    return [];
  } catch {
    return [];
  }
};

export const readLinkedAccounts = (ownerUid) => readRaw(ownerUid);

export const persistLinkedAccounts = (ownerUid, list) => {
  if (!hasWindow()) return list;
  try {
    window.localStorage.setItem(storageKey(ownerUid), JSON.stringify(list));
  } catch {}
  return list;
};

export const encodeSecret = (plain = '') => {
  try {
    const encoder = new TextEncoder();
    const utf8 = encoder.encode(plain);
    let binary = '';
    utf8.forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary);
  } catch {
    return window.btoa(plain);
  }
};

export const decodeSecret = (encoded = '') => {
  try {
    const binary = window.atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  } catch {
    return window.atob(encoded);
  }
};

export const upsertLinkedAccountWithPassword = ({
  ownerUid,
  uid,
  email,
  password,
  pseudo,
  role,
  lastUsedAt,
}) => {
  if (!ownerUid || !uid || !email || !password) {
    return { ok: false, error: 'missing_fields', accounts: readRaw(ownerUid) };
  }
  const accounts = readRaw(ownerUid);
  const existing = accounts.find((acc) => acc.uid === uid);
  const nonOwnerCount = accounts.filter((acc) => acc.uid !== ownerUid).length;
  if (!existing && nonOwnerCount >= MAX_LINKED_ACCOUNTS) {
    return { ok: false, error: 'limit', accounts };
  }
  const now = Date.now();
  const nextEntry = {
    uid,
    email,
    pseudo: pseudo || existing?.pseudo || email,
    role: role || existing?.role || '',
    secret: encodeSecret(password),
    addedAt: existing?.addedAt || now,
    lastUsedAt: lastUsedAt || now,
  };
  const next = [...accounts.filter((acc) => acc.uid !== uid), nextEntry];
  persistLinkedAccounts(ownerUid, next);
  return { ok: true, accounts: next };
};

export const patchLinkedAccount = (ownerUid, uid, patch = {}) => {
  if (!ownerUid || !uid) return { ok: false, accounts: readRaw(ownerUid) };
  const accounts = readRaw(ownerUid);
  let changed = false;
  const next = accounts.map((acc) => {
    if (acc.uid !== uid) return acc;
    changed = true;
    return { ...acc, ...patch };
  });
  if (changed) persistLinkedAccounts(ownerUid, next);
  return { ok: changed, accounts: changed ? next : accounts };
};

export const deleteLinkedAccount = (ownerUid, uid) => {
  if (!ownerUid || !uid) return { ok: false, accounts: readRaw(ownerUid) };
  const accounts = readRaw(ownerUid);
  const next = accounts.filter((acc) => acc.uid !== uid);
  const changed = next.length !== accounts.length;
  if (changed) persistLinkedAccounts(ownerUid, next);
  return { ok: changed, accounts: changed ? next : accounts };
};

export const fetchRemoteLinkedAccounts = async (uid) => {
  if (!uid) return [];
  try {
    const snap = await getDoc(accountsDoc(uid));
    if (!snap.exists()) return [];
    return sanitizeEntries(snap.data()?.accounts || [], uid);
  } catch (err) {
    console.error('[accountLinkStorage] fetchRemoteLinkedAccounts error:', err);
    return [];
  }
};

export const saveRemoteLinkedAccounts = async (uid, accounts) => {
  if (!uid) return;
  const payload = sanitizeEntries(accounts, uid);
  const hasAssociations = payload.some((entry) => entry.uid !== uid);
  try {
    if (!hasAssociations) {
      await deleteDoc(accountsDoc(uid));
      return;
    }
    await setDoc(
      accountsDoc(uid),
      { accounts: payload, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    console.error('[accountLinkStorage] saveRemoteLinkedAccounts error:', err);
    throw err;
  }
};

export const subscribeRemoteLinkedAccounts = (uid, callback) => {
  if (!uid) return () => {};
  return onSnapshot(
    accountsDoc(uid),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback([], false);
        return;
      }
      const list = sanitizeEntries(snapshot.data()?.accounts || [], uid);
      callback(list, true);
    },
    (error) => {
      console.error('[accountLinkStorage] subscribe error:', error);
    }
  );
};

export const upsertRemoteLinkedAccountEntry = async (ownerUid, entry) => {
  if (!ownerUid || !entry?.uid) return [];
  const current = await fetchRemoteLinkedAccounts(ownerUid);
  const next = [entry, ...current.filter((acc) => acc.uid !== entry.uid)];
  await saveRemoteLinkedAccounts(ownerUid, next);
  return next;
};

export const removeRemoteLinkedAccountEntry = async (ownerUid, linkedUid) => {
  if (!ownerUid || !linkedUid) return [];
  const current = await fetchRemoteLinkedAccounts(ownerUid);
  const next = current.filter((acc) => acc.uid !== linkedUid);
  await saveRemoteLinkedAccounts(ownerUid, next);
  return next;
};

export const ensureSelfLinkedAccount = async ({ uid, email, password, pseudo, role }) => {
  if (!uid || !email || !password) return { ok: false };
  const now = Date.now();
  const local = upsertLinkedAccountWithPassword({
    ownerUid: uid,
    uid,
    email,
    password,
    pseudo,
    role,
    lastUsedAt: now,
  });
  const currentRemote = await fetchRemoteLinkedAccounts(uid);
  const hasAssociations = currentRemote.some((acc) => acc.uid !== uid);
  if (!hasAssociations) {
    if (currentRemote.length > 0) {
      await saveRemoteLinkedAccounts(uid, currentRemote);
    }
    return { ok: true, local: local.accounts, remote: currentRemote };
  }
  const selfEntry = {
    uid,
    email,
    pseudo: pseudo || email,
    role: role || '',
    secret: encodeSecret(password),
    addedAt: currentRemote.find((acc) => acc.uid === uid)?.addedAt || now,
    lastUsedAt: now,
    isOwner: true,
  };
  const nextRemote = [selfEntry, ...currentRemote.filter((acc) => acc.uid !== uid)];
  await saveRemoteLinkedAccounts(uid, nextRemote);
  return { ok: true, local: local.accounts, remote: nextRemote };
};
